import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DaypartingTimeparting from './DaypartingTimeparting';
import { useAppDispatch, useAppSelector } from '@/app/store';
import {
  clearSlotEditResult,
  closeCampaignScreenEditModal,
  openSlotEditModal,
} from '../campaignsSlice';
import {
  useCampaignByIdQuery,
  useUpdateCampaignMutation,
} from '../hooks/useCampaignsQueries';
import {
  buildCreativeLookup,
  getCampaignUseFallback,
  transformScreensForEdit,
} from '../utils/campaignTransforms';
import { buildCampaignScreenEditPayload } from '../utils/campaignScreenEditPayload';
import {
  buildSchedulingFromDayTimeParting,
  buildSlotDayTimeParting,
} from '../utils/schedulingTransformer';
import {
  CREATIVE_TYPE,
  isUrlCreative,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';
import { handleSnackbar } from '@/shared/ui';
import { DEFAULT_SCHEDULING } from '../constants';
import type {
  CampaignScreenEditCampaign,
  CampaignScreenEditCreative,
  CampaignScreenEditScreen,
  CampaignScreenEditSlot,
  CampaignSlotCreativeMode,
} from '../types';
import type { FormikProps, ScheduleState } from '@/entities/campaign';
import type { ProcessedSlot, SlotItem } from '@/entities/screen/types';

dayjs.extend(customParseFormat);

const buildScheduleState = (slots: CampaignScreenEditSlot[]) => {
  const firstSlotWithSchedule = slots.find(
    slot => Object.keys(slot.dayTimeParting ?? {}).length > 0
  );

  if (!firstSlotWithSchedule) {
    return DEFAULT_SCHEDULING;
  }

  return (
    buildSchedulingFromDayTimeParting(
      firstSlotWithSchedule.dayTimeParting ?? {}
    ) ?? DEFAULT_SCHEDULING
  );
};

const formatDisplayDate = (value?: string) => {
  if (!value) return 'N/A';

  const parsed = dayjs(value).isValid()
    ? dayjs(value)
    : dayjs(value, ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss.SSS[Z]']);

  return parsed.isValid() ? parsed.format('DD MMM YYYY, hh:mm A') : value;
};

const isNewSlot = (slot: CampaignScreenEditSlot) =>
  slot.originalCreativeIds === undefined;

const normalizeCreatives = (creatives: CampaignScreenEditCreative[]) =>
  creatives.map(creative => {
    const src = typeof creative.src === 'object' ? creative.src : undefined;
    if (!src) return creative;

    return {
      ...creative,
      id: creative.id || src.id,
      name: creative.name || src.name,
      duration: creative.duration ?? src.duration,
      creativeAssetId: creative.creativeAssetId || src.creativeAssetId,
    };
  });

const resolveCreativeMode = (
  creative?: Partial<CampaignScreenEditCreative>
): CampaignSlotCreativeMode => {
  const directType = normalizeCreativeType(creative?.type);
  if (directType === CREATIVE_TYPE.VAST) return CREATIVE_TYPE.VAST;
  if (directType === CREATIVE_TYPE.WEB) return CREATIVE_TYPE.WEB;

  return 'image';
};

const resolveCampaignCreativeMode = (
  campaign: CampaignScreenEditCampaign | null,
  screens: CampaignScreenEditScreen[]
): CampaignSlotCreativeMode => {
  for (const creative of campaign?.creatives ?? []) {
    const mode = resolveCreativeMode(creative);
    if (mode !== 'image') return mode;
  }

  for (const screen of screens) {
    for (const slot of screen.slots ?? []) {
      for (const creative of slot.creatives ?? []) {
        const mode = resolveCreativeMode(creative);
        if (mode !== 'image') return mode;
      }
    }
  }

  return 'image';
};

const mapSlotToProcessedSlot = (
  slot: CampaignScreenEditSlot
): ProcessedSlot => ({
  slotIndex: slot.index ?? 0,
  isEmpty: slot.creatives.length === 0,
  items: slot.creatives.map((creative, creativeIndex): SlotItem => {
    const src = typeof creative.src === 'object' ? creative.src : undefined;

    return {
      id: creative.id || `${slot.index}-${creativeIndex}`,
      type:
        creative.type === CREATIVE_TYPE.VAST
          ? CREATIVE_TYPE.VAST
          : creative.type === CREATIVE_TYPE.WEB
            ? CREATIVE_TYPE.WEB
            : creative.type === 'VIDEO' || src?.type === 'VIDEO'
              ? 'VIDEO'
              : 'IMAGE',
      title: creative.name || 'Creative',
      src: typeof creative.src === 'string' ? creative.src : src?.src || '',
      duration: creative.duration ?? 0,
      priority: creativeIndex,
      campaignId: '',
      creativeAssetId: creative.creativeAssetId || '',
      bookingSource: 'SCREEN_OWNER',
      originalSlotIndex: slot.index ?? 0,
    };
  }),
});

const buildNextSlotIndex = (slots: CampaignScreenEditSlot[]) => {
  const usedIndexes = new Set(
    slots
      .map(slot => slot.index)
      .filter((index): index is number => typeof index === 'number')
  );
  let nextIndex = 0;
  while (usedIndexes.has(nextIndex)) {
    nextIndex += 1;
  }
  return nextIndex;
};

const CampaignScreenEditModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const modalState = useAppSelector(
    state => state.dooh.campaigns.campaignScreenEditModal
  );
  const slotEditModalState = useAppSelector(
    state => state.dooh.campaigns.slotEditModal
  );
  const partnerId = useAppSelector(state => state.auth.user?.partner_id);
  const updateCampaignMutation = useUpdateCampaignMutation();

  const isOpen = Boolean(
    modalState.isOpen && modalState.campaignId && modalState.screenId
  );

  const { data, isLoading, error } = useCampaignByIdQuery(
    modalState.campaignId,
    {
      enabled: isOpen,
    }
  );

  const campaign = data?.data ?? null;
  const transformedScreens = useMemo(() => {
    if (!campaign?.screens) return [];
    return transformScreensForEdit(
      campaign.screens,
      buildCreativeLookup(campaign.creatives)
    ) as CampaignScreenEditScreen[];
  }, [campaign]);

  const selectedScreen = useMemo(() => {
    if (!modalState.screenId) return undefined;
    return transformedScreens.find(screen => screen.id === modalState.screenId);
  }, [modalState.screenId, transformedScreens]);

  const [localSlots, setLocalSlots] = useState<CampaignScreenEditSlot[]>([]);
  const [schedule, setSchedule] = useState<ScheduleState>(DEFAULT_SCHEDULING);
  const [hasScheduleChange, setHasScheduleChange] = useState(false);
  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
  const [draftSlotIndex, setDraftSlotIndex] = useState<number | null>(null);
  const savedDraftSlotIndexRef = useRef<number | null>(null);
  const wasSlotEditModalOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !selectedScreen) return;

    const nextSlots = selectedScreen.slots.map(slot => ({
      ...slot,
      creatives: normalizeCreatives(slot.creatives),
    }));
    const scheduleState = buildScheduleState(nextSlots);

    setLocalSlots(nextSlots);
    setSchedule(scheduleState);
    setHasScheduleChange(false);
    setEditingSlotIndex(null);
    setDraftSlotIndex(null);
    savedDraftSlotIndexRef.current = null;
  }, [isOpen, selectedScreen]);

  const campaignCreativeMode = useMemo(
    () => resolveCampaignCreativeMode(campaign, transformedScreens),
    [campaign, transformedScreens]
  );
  const campaignUseFallback = campaign
    ? getCampaignUseFallback(campaign.creatives)
    : false;
  const brandFolderId =
    campaign?.brandCreative ?? campaign?.selectedBrand?.id ?? '';

  const schedulingFormik = useMemo(
    () =>
      ({
        values: { scheduling: schedule },
        errors: {},
        touched: {},
        isLoading: false,
        setField: (field: string, value: ScheduleState) => {
          if (field !== 'scheduling') return;
          setSchedule(value);
          setHasScheduleChange(true);
        },
      }) as FormikProps,
    [schedule]
  );

  const maxSlots = selectedScreen?.numberOfSlots ?? 0;
  const canAddSlot = maxSlots === 0 || localSlots.length < maxSlots;
  const slotDuration = selectedScreen?.slotTimeInSec ?? 0;
  const isSaving = updateCampaignMutation.isPending;

  const handleClose = () => {
    if (isSaving) return;
    dispatch(closeCampaignScreenEditModal());
  };

  const handleDeleteSlot = (slotIndex: number) => {
    setLocalSlots(prev => prev.filter(slot => slot.index !== slotIndex));
  };

  const applyLocalSlotSave = (
    slotIndex: number,
    creatives: CampaignScreenEditCreative[]
  ) => {
    if (draftSlotIndex === slotIndex && creatives.length === 0) {
      setLocalSlots(prev => prev.filter(slot => slot.index !== slotIndex));
      setDraftSlotIndex(null);
      return;
    }

    if (draftSlotIndex === slotIndex) {
      savedDraftSlotIndexRef.current = slotIndex;
      setDraftSlotIndex(null);
    }

    setLocalSlots(prev =>
      prev.map(slot =>
        slot.index === slotIndex
          ? { ...slot, creatives: normalizeCreatives(creatives) }
          : slot
      )
    );
  };

  const handleSlotEditorClose = () => {
    if (
      draftSlotIndex !== null &&
      editingSlotIndex === draftSlotIndex &&
      savedDraftSlotIndexRef.current !== draftSlotIndex
    ) {
      setLocalSlots(prev =>
        prev.filter(
          slot => slot.index !== draftSlotIndex || slot.creatives.length > 0
        )
      );
    }

    if (savedDraftSlotIndexRef.current === draftSlotIndex) {
      savedDraftSlotIndexRef.current = null;
    }

    setDraftSlotIndex(null);
    setEditingSlotIndex(null);
  };

  const openSlotEditor = (slot: CampaignScreenEditSlot) => {
    const slotIndex = slot.index ?? 0;

    setEditingSlotIndex(slotIndex);
    dispatch(
      openSlotEditModal({
        slotIndex,
        slot: mapSlotToProcessedSlot(slot),
        campaignId: modalState.campaignId || '',
        deviceId: modalState.screenId || '',
        brandFolderId,
        campaignUseFallback,
        localSaveMode: true,
        lockedCreativeMode: campaignCreativeMode,
        initialCreatives: slot.creatives,
      })
    );
  };

  const handleAddSlot = () => {
    if (!canAddSlot) return;

    const nextIndex = buildNextSlotIndex(localSlots);
    const currentDayTimeParting = buildSlotDayTimeParting(schedule);
    const nextSlot = {
      name: `Slot ${nextIndex + 1}`,
      index: nextIndex,
      dayTimeParting: currentDayTimeParting,
      originalDayTimeParting: currentDayTimeParting,
      creatives: [],
    };

    setLocalSlots(prev => [...prev, nextSlot]);
    setDraftSlotIndex(nextIndex);
    openSlotEditor(nextSlot);
  };

  useEffect(() => {
    if (
      slotEditModalState.savedCreatives === null ||
      slotEditModalState.slotIndex === null
    ) {
      return;
    }

    applyLocalSlotSave(
      slotEditModalState.slotIndex,
      slotEditModalState.savedCreatives
    );
    dispatch(clearSlotEditResult());
  }, [
    dispatch,
    slotEditModalState.savedCreatives,
    slotEditModalState.slotIndex,
    draftSlotIndex,
  ]);

  useEffect(() => {
    if (slotEditModalState.isOpen) {
      wasSlotEditModalOpenRef.current = true;
      return;
    }

    if (wasSlotEditModalOpenRef.current) {
      wasSlotEditModalOpenRef.current = false;
      handleSlotEditorClose();
    }
  }, [slotEditModalState.isOpen]);

  const handleSave = async () => {
    if (
      !campaign ||
      !selectedScreen ||
      !modalState.campaignId ||
      !modalState.screenId
    ) {
      return;
    }

    if (!partnerId) {
      dispatch(
        handleSnackbar({
          open: true,
          message: 'Partner ID is required to save campaign screen changes.',
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      return;
    }

    const hasEmptyNewSlot = localSlots.some(
      slot => isNewSlot(slot) && slot.creatives.length === 0
    );

    if (hasEmptyNewSlot) {
      dispatch(
        handleSnackbar({
          open: true,
          message: 'Add creatives before saving a new slot.',
          severity: 'warning',
          autoHideDuration: 3000,
        })
      );
      return;
    }

    const hasMissingFallback =
      campaignUseFallback &&
      localSlots.some(slot =>
        slot.creatives.some(creative => {
          const type = normalizeCreativeType(creative.type);
          return (
            isUrlCreative(type) &&
            !creative.fallbackCreativeId
          );
        })
      );

    if (hasMissingFallback) {
      dispatch(
        handleSnackbar({
          open: true,
          message:
            'Choose a fallback creative for every WEB or VAST creative before saving.',
          severity: 'warning',
          autoHideDuration: 3500,
        })
      );
      return;
    }

    try {
      const payload = buildCampaignScreenEditPayload({
        campaign,
        screenId: modalState.screenId,
        partnerId,
        editedSlots: localSlots,
        screenScheduleChange: hasScheduleChange ? schedule : undefined,
        useFallback: campaignUseFallback,
      });

      await updateCampaignMutation.mutateAsync({
        campaignId: modalState.campaignId,
        payload,
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['campaign', modalState.campaignId],
        }),
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({
          queryKey: ['screen', modalState.screenId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['screenPlaylist', modalState.screenId],
        }),
      ]);

      dispatch(
        handleSnackbar({
          open: true,
          message: 'Campaign screen changes saved.',
          severity: 'success',
          autoHideDuration: 3000,
        })
      );
      dispatch(closeCampaignScreenEditModal());
    } catch (saveError) {
      dispatch(
        handleSnackbar({
          open: true,
          message:
            saveError instanceof Error
              ? saveError.message
              : 'Failed to save campaign screen changes.',
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
    }
  };

  const totalCreatives = localSlots.reduce(
    (count, slot) => count + slot.creatives.length,
    0
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">Failed to load campaign details.</Alert>;
    }

    if (!campaign || !selectedScreen) {
      return (
        <Alert severity="warning">
          Selected campaign or screen details were not found.
        </Alert>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Campaign Information
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Campaign Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {campaign.name || modalState.campaignName || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Screen Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {String(selectedScreen.name || modalState.screenName || 'N/A')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Campaign Date Range
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDisplayDate(campaign.startDate)} -{' '}
                {formatDisplayDate(campaign.endDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Slot Duration
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {slotDuration ? `${slotDuration} seconds` : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fallback Playback
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Switch checked={campaignUseFallback} disabled size="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {campaignUseFallback ? 'On' : 'Off'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box>
          <DaypartingTimeparting formik={schedulingFormik} />
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Booked Slots
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {localSlots.length} slot{localSlots.length === 1 ? '' : 's'} •{' '}
                {totalCreatives} creative{totalCreatives === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddSlot}
              disabled={!canAddSlot}
            >
              Add Slot
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {localSlots.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No booked slots on this screen.
                </Typography>
              </Paper>
            ) : (
              localSlots
                .slice()
                .sort((left, right) => (left.index ?? 0) - (right.index ?? 0))
                .map(slot => (
                  <Paper
                    key={slot.index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {slot.name || `Slot ${(slot.index ?? 0) + 1}`}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {slot.creatives.length} creative
                          {slot.creatives.length === 1 ? '' : 's'}
                        </Typography>
                        {isNewSlot(slot) && (
                          <Chip label="New" size="small" color="primary" />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        aria-label="Edit slot creatives"
                        color="primary"
                        onClick={() => openSlotEditor(slot)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete slot"
                        color="error"
                        onClick={() => handleDeleteSlot(slot.index ?? 0)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Edit Campaign On Screen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {modalState.campaignName || 'Selected campaign'}
            {' - '}
            {modalState.screenName || 'Selected screen'}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close campaign screen edit modal"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || isLoading || Boolean(error) || !campaign}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignScreenEditModal;
